import { APIClient } from "./apiCore";
import api from "../utils/api"; // Centralized API instance with token refresh
import * as url from "./urls";

// Types
// Webhook Types
export interface WebhookVerificationConfig {
  challenge_param?: string;
  token_param?: string;
  token?: string;
  secret?: string;
  signature_header?: string;
  signature_algo?: string;
  secret_env_var?: string;
  pubsub_topic?: string;
}

export interface WebhookEventType {
  type: string;
  label: string;
  description?: string;
}

export interface WebhookConfig {
  enabled: boolean;
  subscribe_url?: string;
  unsubscribe_url?: string;
  renew_url?: string;
  verification_method?:
    | "challenge"
    | "signature"
    | "token"
    | "none"
    | "client_state";
  verification_config?: WebhookVerificationConfig;
  default_events?: string[];
  available_events?: WebhookEventType[];
  subscription_ttl_hours?: number;
  request_format?: "json" | "form";
  auth_method?: "bearer" | "basic" | "api_key";
  extra_subscribe_params?: Record<string, any>;
}

export interface OAuthConnector {
  id: string;
  name: string;
  provider: string;
  description: string;
  image_url?: string;
  client_id?: string; // Only included for admin users
  auth_url: string;
  token_url: string;
  api_url: string;
  scopes: string[];
  webhook_config?: WebhookConfig;
  status: "active" | "inactive";
  created_at: string;
  updated_at: string;
}

export interface OAuthConnection {
  id: string;
  company_id: string;
  connector_id: string;
  token_type: string;
  expires_at: string;
  scopes: string[];
  status: "active" | "inactive" | "expired";
  created_by: string;
  last_refreshed_at: string;
  created_at: string;
  updated_at: string;
  connector: OAuthConnector;
  metadata?: {
    workspace_name?: string;
    email?: string;
    user_name?: string;
    cloud_id?: string;
    site_name?: string;
    accessible_resources?: Array<{ id: string; name: string; url: string }>;
    [key: string]: any;
  };
}

export interface ConnectorFormData {
  name: string;
  provider: string;
  description: string;
  image_url?: string;
  client_id: string;
  client_secret: string;
  auth_url: string;
  token_url: string;
  api_url: string;
  scopes: string[];
  extra_params?: Record<string, any>;
  webhook_config?: WebhookConfig;
}

// Webhook Subscription Types
export interface WebhookSubscription {
  id: string;
  connection_id: string;
  provider_subscription_id?: string;
  event_types: string[];
  resource_id?: string;
  status: "pending" | "active" | "expired" | "failed" | "paused";
  expires_at?: string;
  last_renewed_at?: string;
  renewal_attempts: number;
  provider_data?: Record<string, any>;
  error_message?: string;
  last_error_at?: string;
  created_at: string;
  updated_at: string;
  email?: string;
}

export interface WebhookEvent {
  id: string;
  subscription_id?: string;
  connection_id?: string;
  company_id?: string;
  provider: string;
  event_type?: string;
  event_id?: string;
  raw_payload?: Record<string, any>;
  parsed_data?: Record<string, any>;
  status: "received" | "processing" | "delivered" | "failed";
  delivered_at?: string;
  error_message?: string;
  source_ip?: string;
  created_at: string;
}

export interface AuthorizationResponse {
  authorization_url: string;
  state: string;
}

export interface TestConnectionResponse {
  success: boolean;
  data?: any;
  error?: string;
}

// Use centralized API instance that includes token refresh logic
// const apiClient = new APIClient(); // Keeping reference for compatibility, but using centralized api

// Connector Management APIs (Admin Only)
export const getConnectors = (): Promise<OAuthConnector[]> => {
  return api
    .get(url.GET_OAUTH_CONNECTORS)
    .then((response) => response.data) as any;
};

export const createConnector = (
  data: ConnectorFormData,
): Promise<OAuthConnector> => {
  return api
    .post(url.CREATE_OAUTH_CONNECTOR, data)
    .then((response) => response.data) as any;
};

export const updateConnector = (
  id: string,
  data: ConnectorFormData,
): Promise<OAuthConnector> => {
  return api
    .put(url.UPDATE_OAUTH_CONNECTOR + "/" + id, data)
    .then((response) => response.data) as any;
};

export const deleteConnector = (id: string): Promise<any> => {
  return api
    .delete(url.DELETE_OAUTH_CONNECTOR + "/" + id)
    .then((response) => response.data) as any;
};

// Connection Management APIs
export const getConnections = (): Promise<OAuthConnection[]> => {
  return api
    .get(url.GET_OAUTH_CONNECTIONS)
    .then((response) => response.data) as any;
};

export const getConnectorsWithStatus = async () => {
  const [connectors, connections] = await Promise.all([
    getConnectors(),
    getConnections(),
  ]);

  // Map connectors with connection status
  return connectors.map((connector: OAuthConnector) => ({
    ...connector,
    isConnected: connections.some(
      (conn: OAuthConnection) =>
        conn.connector_id === connector.id && conn.status === "active",
    ),
    connection: connections.find(
      (conn: OAuthConnection) => conn.connector_id === connector.id,
    ),
  }));
};

export const initiateOAuthFlow = (
  connectorId: string,
  params?: {
    redirect_uri?: string;
    prompt?: string;
    access_type?: string;
    login_hint?: string;
    hd?: string;
    [key: string]: any; // Allow other OAuth parameters
  },
): Promise<AuthorizationResponse> => {
  // Build query string with redirect_uri
  const queryParams = new URLSearchParams();
  if (params?.redirect_uri) {
    queryParams.append("redirect_uri", params.redirect_uri);
  }

  const endpoint =
    url.INITIATE_OAUTH_FLOW +
    "/" +
    connectorId +
    "/authorize" +
    (queryParams.toString() ? "?" + queryParams.toString() : "");

  // Build request body with OAuth parameters (excluding redirect_uri which goes in query)
  const requestBody: Record<string, any> = {};
  if (params) {
    Object.keys(params).forEach((key) => {
      if (key !== "redirect_uri" && params[key] !== undefined) {
        requestBody[key] = params[key];
      }
    });
  }

  // Send POST with request body (even if empty, to match API spec)
  return api
    .post(
      endpoint,
      Object.keys(requestBody).length > 0 ? requestBody : undefined,
    )
    .then((response) => response.data) as any;
};

export const disconnectConnection = (connectorId: string): Promise<any> => {
  return api
    .delete(url.DELETE_OAUTH_CONNECTION + "/" + connectorId)
    .then((response) => response.data) as any;
};

export interface EmailConnection {
  connection_id: string;
  email: string;
}

export interface EmailsResponse {
  emails: EmailConnection[];
}

// Get emails for a specific connector (e.g., Gmail)
export const getConnectorEmails = (
  connectorProvider: string,
): Promise<EmailsResponse> => {
  return api
    .get(`/api/oauth/connections/${connectorProvider}/emails`)
    .then((response) => response.data) as any;
};

// Delete a connection by connection_id
export const deleteConnectionById = (connectionId: string): Promise<any> => {
  return api
    .delete(`/api/oauth/connections/connection/${connectionId}`)
    .then((response) => response.data) as any;
};

export const refreshConnection = (
  connectorId: string,
): Promise<OAuthConnection> => {
  return api
    .post(url.REFRESH_OAUTH_CONNECTION + "/" + connectorId + "/refresh")
    .then((response) => response.data) as any;
};

export const testConnection = async (
  connectorId: string,
): Promise<TestConnectionResponse> => {
  try {
    const response = await api.get(
      url.TEST_OAUTH_CONNECTION + "/" + connectorId + "/me",
    );
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Connection test failed",
    };
  }
};

// Atlassian site selection for connections with multiple accessible resources
export const selectAtlassianSite = (
  connectionId: string,
  cloudId: string,
): Promise<any> => {
  return api
    .post(`/api/oauth/connections/connection/${connectionId}/select-site`, {
      cloud_id: cloudId,
    })
    .then((response) => response.data) as any;
};

// Proxy API for making authenticated requests through connections
export const proxyRequest = (
  connectorId: string,
  endpoint: string,
  method = "GET",
  data?: any,
): Promise<any> => {
  const proxyUrl =
    url.OAUTH_PROXY_REQUEST +
    "/" +
    connectorId +
    "/" +
    endpoint.replace(/^\//, "");

  switch (method.toUpperCase()) {
    case "POST":
      return api.post(proxyUrl, data).then((response) => response.data) as any;
    case "PUT":
      return api.put(proxyUrl, data).then((response) => response.data) as any;
    case "DELETE":
      return api.delete(proxyUrl).then((response) => response.data) as any;
    case "GET":
    default:
      return api.get(proxyUrl).then((response) => response.data) as any;
  }
};

// Utility function to handle OAuth errors
export const handleOAuthError = (error: string): string => {
  switch (error) {
    case "access_denied":
      return "Authorization was denied. Please try again and grant the required permissions.";
    case "invalid_request":
      return "Invalid authorization request. Please contact support.";
    case "unauthorized_client":
      return "This application is not authorized. Please contact support.";
    case "unsupported_response_type":
      return "Authorization method not supported. Please contact support.";
    case "invalid_scope":
      return "Requested permissions are not valid. Please contact support.";
    case "server_error":
      return "Server error occurred during authorization. Please try again later.";
    case "temporarily_unavailable":
      return "Authorization service is temporarily unavailable. Please try again later.";
    default:
      return `Authorization failed: ${error}`;
  }
};

// Utility function to format OAuth scopes into readable permissions
export const formatScopePermissions = (scopes: string[]): string[] => {
  return scopes.map((scope) => {
    // Gmail scopes
    if (scope.includes("gmail.readonly")) return "Read your emails";
    if (scope.includes("gmail.modify")) return "Read and modify your emails";
    if (scope.includes("gmail.send")) return "Send emails on your behalf";
    if (scope.includes("gmail.compose")) return "Create draft emails";

    // Google Calendar scopes
    if (scope.includes("calendar.readonly")) return "Read your calendar events";
    if (scope.includes("calendar"))
      return "Read and manage your calendar events";

    // Slack scopes
    if (scope.includes("channels:read")) return "Read channel information";
    if (scope.includes("channels:write")) return "Create and manage channels";
    if (scope.includes("chat:write")) return "Send messages";
    if (scope.includes("users:read")) return "Read user information";
    if (scope.includes("files:read")) return "Access shared files";
    if (scope.includes("files:write")) return "Upload and share files";

    // Microsoft Graph scopes
    if (scope.includes("mail.read")) return "Read your emails";
    if (scope.includes("mail.send")) return "Send emails on your behalf";
    if (scope.includes("calendars.read")) return "Read your calendar events";
    if (scope.includes("calendars.readwrite"))
      return "Read and manage your calendar events";
    if (scope.includes("user.read")) return "Read your profile information";

    // Generic fallbacks
    if (scope.includes("read"))
      return `Read access to ${scope.split(".")[0] || "data"}`;
    if (scope.includes("write"))
      return `Write access to ${scope.split(".")[0] || "data"}`;

    // Return the last part of the scope or the full scope
    const parts = scope.split("/");
    return parts[parts.length - 1] || scope;
  });
};

// Utility function to get connection display name
export const getConnectionDisplayName = (
  connection: OAuthConnection,
  connector: OAuthConnector,
): string => {
  if (connection.metadata) {
    const { metadata } = connection;
    if (metadata.workspace_name) {
      return `${connector.name} (${metadata.workspace_name})`;
    }
    if (metadata.email) {
      return `${connector.name} (${metadata.email})`;
    }
    if (metadata.user_name) {
      return `${connector.name} (${metadata.user_name})`;
    }
  }

  return connector.name;
};

// Webhook Subscription Management APIs
export const getWebhookSubscriptions = (
  connectorId: string,
): Promise<WebhookSubscription[]> => {
  return api
    .get(`/api/oauth/webhooks/connectors/${connectorId}/subscriptions`)
    .then((response) => response.data) as any;
};

export const getWebhookEvents = (
  connectionId?: string,
  limit = 50,
  offset = 0,
): Promise<WebhookEvent[]> => {
  let endpoint = `${url.GET_WEBHOOK_EVENTS}?limit=${limit}&offset=${offset}`;
  if (connectionId) {
    endpoint += `&connection_id=${connectionId}`;
  }
  return api.get(endpoint).then((response) => response.data) as any;
};

export const pauseWebhookSubscription = (
  subscriptionId: string,
): Promise<void> => {
  return api
    .post(`${url.WEBHOOK_SUBSCRIPTIONS}/${subscriptionId}/pause`)
    .then((response) => response.data) as any;
};

export const resumeWebhookSubscription = (
  subscriptionId: string,
): Promise<void> => {
  return api
    .post(`${url.WEBHOOK_SUBSCRIPTIONS}/${subscriptionId}/resume`)
    .then((response) => response.data) as any;
};

export const renewWebhookSubscription = (
  subscriptionId: string,
): Promise<void> => {
  return api
    .post(`${url.WEBHOOK_SUBSCRIPTIONS}/${subscriptionId}/renew`)
    .then((response) => response.data) as any;
};

export const deleteWebhookSubscription = (
  subscriptionId: string,
): Promise<void> => {
  return api
    .delete(`${url.WEBHOOK_SUBSCRIPTIONS}/${subscriptionId}`)
    .then((response) => response.data) as any;
};

// Utility function to get webhook status color
export const getWebhookStatusColor = (
  status: WebhookSubscription["status"],
): "success" | "warning" | "error" | "default" => {
  switch (status) {
    case "active":
      return "success";
    case "pending":
    case "paused":
      return "warning";
    case "failed":
    case "expired":
      return "error";
    default:
      return "default";
  }
};

// Utility function to format webhook event types
export const formatWebhookEventType = (eventType: string): string => {
  // Convert snake_case or dot.notation to readable format
  return eventType
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
};
